import type { ClassId, SynergyEffect } from '@prism/sim';
import type { TowerDef } from '../schemas/defs.js';

/**
 * The 37 towers — the original circulant 30, plus the archetype expansion (B-15/17/18/19).
 *
 * ── Why the class pairs look systematic ────────────────────────────────────────────────
 * Every tower carries exactly 2 classes (R-02-06), so 30 towers yield 60 class-slots over
 * 10 classes — exactly 6 each, which is precisely the tier-6 threshold. There is zero
 * slack: if any class ended up with 5 towers, its top tier would be *unreachable* and a
 * tenth of the design would be dead content.
 *
 * Rather than hope a hand-assigned list balances, the pairs are laid out as a circulant
 * graph: with classes indexed 0-9 around a ring, tower (i, d) joins class i to class
 * (i+d) mod 10 for d ∈ {1,2,3}. Every class then has degree exactly 6 by construction —
 * 3 as the near class, 3 as the far one. V-2 verifies it anyway.
 *
 * Tier tracks ring distance: adjacent classes are tier 1, distance-3 pairs are tier 3.
 * That is arbitrary but not meaningless — it puts the most thematically distant
 * combinations at the top of the shop, where they read as the exotic finds.
 *
 * ── Why they no longer all shoot the same dot ─────────────────────────────────────────
 * `projectileKind` alone gave thirty towers four silhouettes, sixteen of them the same
 * bullet. The firing approach (TRD-05 §5.1) is the second axis, and it is what a player
 * actually reads on the battlefield:
 *
 *   pierce      palisade-bow, grave-anchor, toxin-lance, frostmark — reward a long lane
 *   laser       rimeblight, frost-mint, winter-choir — sustained, low per-tick, ramping
 *   salvo       hivelight, coin-hive, wasp-battery — damage DIVIDED across a fan, so
 *               they are devastating point-blank into a clump and poor at a straggler
 *   burst       gilded-lamp, bastion-nest, aegis-lantern — bunched output, long reload
 *   missile     umbral-beacon, entropy-mint, oblivion-rune — homing + AoE + acceleration,
 *               slow off the rail and unavoidable by the time it lands
 *
 * ⚠ Stats here are PLACEHOLDER. Shape is TRD-02's job; balance is an iterative concern
 * that needs a playable game (TRD-05/06/07) to tune against. The retune above preserves
 * each tower's rough single-target DPS — it changes how damage ARRIVES, not how much.
 */

interface TowerSpec {
  id: string;
  name: string;
  glyph: string;
  classes: [ClassId, ClassId];
  tier: 1 | 2 | 3;
  damage: number;
  kind: 'physical' | 'magic' | 'true';
  interval: number;
  range: number;
  projectile: 'bullet' | 'homing' | 'beam' | 'laser' | 'lob' | 'chain' | 'zone';
  speed: number;
  aoe?: number;
  /**
   * Firing approach (TRD-05 §5.1) — the axis that makes thirty towers feel like thirty
   * weapons rather than thirty tints of the same dot. Every field is optional and
   * defaults to the identity, so a tower that wants to be a plain gun says nothing.
   */
  salvo?: number;
  /** Total fan angle, degrees at the authoring layer because nobody thinks in radians. */
  spreadDeg?: number;
  burst?: number;
  burstGapTicks?: number;
  pierce?: number;
  /** Tiles/s². A missile launches at `speed` and builds to 3× it. */
  accel?: number;
  /** `laser` only: +multiplier per damage tick held on one enemy, and its ceiling. */
  rampPerTick?: number;
  rampMax?: number;
  /** `zone` only (B-18/B-19): telegraph, active window, strike cadence, drift speed. */
  zone?: { windup?: number; active?: number; strikeEvery?: number; trackSpeed?: number };
  /** B-15 — a pure buffer: damage/interval 0, never fires, power lives in `innate`. */
  support?: boolean;
  /** B-15/B-17 — the spec's authored identity, evaluated with the tower as sole member. */
  innate?: SynergyEffect[];
  targeting?: 'first' | 'last' | 'closest' | 'strongest' | 'weakest';
  /** Where this tower peaks. Omitted means `even` — see STAR_CURVES. */
  scaling?: ScalingRole;
  description: string;
}

/**
 * Star curves — the axis that gives a tower a PLACE IN THE RUN (playtest request).
 *
 * A single global curve made every tower the same shape: whatever was strongest on paper
 * was strongest at every star, so the board you built on wave 3 was the board you wanted
 * on wave 25 and merging was a formality. Three curves fix that by trading base power
 * against merge payoff:
 *
 *   rush    strong right now, a thin merge — wins the opening, then earns its sale
 *   even    the original curve; the honest middle
 *   scaler  weak in the shop, monstrous merged — dead weight early, a win condition late
 *
 * Merging already costs 3 copies for one board slot, so a `scaler` at ★3 SHOULD embarrass
 * a `rush` at ★3. The point is that it loses to it at ★1, which is what makes selling the
 * opener a real decision rather than a chore.
 */
const STAR_CURVES = {
  rush: [1, 1.4, 1.9],
  even: [1, 1.8, 3.2],
  scaler: [1, 2.3, 5.0],
} as const;

/**
 * Range's own star curve, on the same roles (playtest request).
 *
 * A merge that only added damage left a badly-placed tower badly placed. Widening reach
 * is what turns merging into an upgrade to the BOARD rather than to one number — and for
 * a support tower, whose aura IS its range, it is the only thing a merge can give it.
 *
 * Weighted the same way the damage curves are, so a role keeps one identity: `scaler`
 * pays off late in both, `rush` is already what it is going to be. Much gentler numbers
 * than the damage curves, because range is quadratic in what it covers — the 1.32 at the
 * bottom right is already about 1.7× the area.
 */
const RANGE_CURVES = {
  rush: [1, 1.03, 1.06],
  even: [1, 1.08, 1.16],
  scaler: [1, 1.15, 1.32],
} as const;

type ScalingRole = keyof typeof STAR_CURVES;

const DEG = Math.PI / 180;

function build(spec: TowerSpec): TowerDef {
  return {
    id: spec.id,
    name: spec.name,
    tier: spec.tier,
    classes: spec.classes,
    glyph: spec.glyph,
    description: spec.description,
    base: {
      damage: spec.damage,
      damageKind: spec.kind,
      attackInterval: spec.interval,
      range: spec.range,
      projectile: {
        kind: spec.projectile,
        speed: spec.speed,
        radius: 0.12,
        // Homing needs a turn rate to steer at all. A missile turns SLOWER than a seeker
        // bolt — that lazy arc is most of what reads as "missile" rather than "bullet
        // that cheats", and it is why fast flankers can out-turn one.
        turnRate: spec.projectile === 'homing' ? (spec.accel ? 3 : 6) : 0,
        aoeRadius: spec.aoe ?? 0,
        salvo: spec.salvo ?? 1,
        spreadRadians: (spec.spreadDeg ?? 0) * DEG,
        burstCount: spec.burst ?? 1,
        burstIntervalTicks: spec.burstGapTicks ?? 4,
        pierce: spec.pierce ?? 0,
        acceleration: spec.accel ?? 0,
        beamRampPerTick: spec.rampPerTick ?? 0,
        beamRampMax: spec.rampMax ?? 1,
        zoneWindupTicks: spec.zone?.windup ?? 30,
        zoneActiveTicks: spec.zone?.active ?? 120,
        zoneStrikeEveryTicks: spec.zone?.strikeEvery ?? 20,
        zoneTrackSpeed: spec.zone?.trackSpeed ?? 0,
      },
      defaultTargeting: spec.targeting ?? 'first',
    },
    starScaling: (() => {
      const c = STAR_CURVES[spec.scaling ?? 'even'];
      return [c[0], c[1], c[2]];
    })(),
    rangeScaling: (() => {
      const c = RANGE_CURVES[spec.scaling ?? 'even'];
      return [c[0], c[1], c[2]];
    })(),
    onFire: [],
    onHit: [],
    onKill: [],
    support: spec.support ?? false,
    innateEffects: spec.innate ?? [],
  };
}

const SPECS: TowerSpec[] = [
  // ── Tier 1 — adjacent classes (ring distance 1) ────────────────────────────────────
  {
    id: 'palisade-bow',
    name: 'Palisade Bow',
    glyph: 'Pb',
    classes: ['warden', 'marksman'],
    tier: 1,
    damage: 15,
    kind: 'physical',
    interval: 1.0,
    range: 4.0,
    projectile: 'bullet',
    speed: 12,
    pierce: 1,
    scaling: 'rush',
    description: 'A braced crossbow whose bolt punches clean through the first body.',
  },
  {
    id: 'runeshot',
    name: 'Runeshot',
    glyph: 'Rs',
    classes: ['marksman', 'arcanist'],
    tier: 1,
    damage: 10,
    kind: 'magic',
    interval: 0.9,
    range: 3.8,
    projectile: 'bullet',
    speed: 14,
    description: 'Etched bolts that shrug off plating.',
  },
  {
    id: 'blightcaster',
    name: 'Blightcaster',
    glyph: 'Bc',
    classes: ['arcanist', 'venom'],
    tier: 1,
    damage: 10,
    kind: 'magic',
    interval: 0.85,
    range: 3.5,
    projectile: 'lob',
    speed: 8,
    aoe: 0.8,
    scaling: 'rush',
    description: 'Lobs a bursting flask of spellrot.',
  },
  {
    id: 'rimeblight',
    name: 'Rimeblight',
    glyph: 'Rb',
    classes: ['venom', 'frost'],
    tier: 1,
    damage: 2,
    kind: 'magic',
    interval: 0.15,
    range: 3.4,
    projectile: 'laser',
    speed: 0,
    scaling: 'scaler',
    description: 'A thin rime-beam it holds on one target until the target stops moving.',
  },
  {
    id: 'sleetcoil',
    name: 'Sleetcoil',
    glyph: 'Sc',
    classes: ['frost', 'tempest'],
    tier: 1,
    damage: 14,
    kind: 'magic',
    interval: 0.8,
    range: 3.6,
    projectile: 'chain',
    speed: 20,
    scaling: 'scaler',
    description: 'Arcs of supercooled charge.',
  },
  {
    id: 'coin-arc',
    name: 'Coin Arc',
    glyph: 'Ca',
    classes: ['tempest', 'alchemist'],
    tier: 1,
    damage: 15,
    kind: 'physical',
    interval: 0.9,
    range: 3.5,
    projectile: 'chain',
    speed: 20,
    scaling: 'rush',
    description: 'Flings charged specie that leaps between targets.',
  },
  {
    id: 'gilded-lamp',
    name: 'Gilded Lamp',
    glyph: 'Gl',
    classes: ['alchemist', 'beacon'],
    tier: 1,
    damage: 10,
    kind: 'physical',
    interval: 1.4,
    range: 3.2,
    projectile: 'bullet',
    speed: 10,
    burst: 2,
    burstGapTicks: 5,
    scaling: 'rush',
    description: 'Fires twice, then spends a long moment recasting its lens.',
  },
  {
    id: 'hivelight',
    name: 'Hivelight',
    glyph: 'Hl',
    classes: ['beacon', 'swarm'],
    tier: 1,
    damage: 35,
    kind: 'physical',
    interval: 0.7,
    range: 3.0,
    projectile: 'bullet',
    speed: 10,
    salvo: 3,
    spreadDeg: 14,
    scaling: 'rush',
    description: 'Scatters three motes of light in a shallow fan.',
  },
  {
    id: 'nullbrood',
    name: 'Nullbrood',
    glyph: 'Nb',
    classes: ['swarm', 'void'],
    tier: 1,
    damage: 9,
    kind: 'magic',
    interval: 0.8,
    range: 3.2,
    projectile: 'bullet',
    speed: 11,
    scaling: 'scaler',
    description: 'Hatches little absences.',
  },
  {
    id: 'grave-anchor',
    name: 'Grave Anchor',
    glyph: 'Ga',
    classes: ['void', 'warden'],
    tier: 1,
    damage: 15,
    kind: 'magic',
    interval: 1.2,
    range: 3.4,
    projectile: 'bullet',
    speed: 9,
    targeting: 'strongest',
    pierce: 2,
    scaling: 'rush',
    description: 'A grave-iron slug that drags through a whole file of them.',
  },

  // ── Tier 2 — ring distance 2 ───────────────────────────────────────────────────────
  {
    id: 'ward-sigil',
    name: 'Ward Sigil',
    glyph: 'Ws',
    classes: ['warden', 'arcanist'],
    tier: 2,
    damage: 20,
    kind: 'magic',
    interval: 1.0,
    range: 4.2,
    projectile: 'homing',
    speed: 10,
    scaling: 'rush',
    description: 'A standing glyph that hunts what it marks.',
  },
  {
    id: 'toxin-lance',
    name: 'Toxin Lance',
    glyph: 'Tl',
    classes: ['marksman', 'venom'],
    tier: 2,
    damage: 16,
    kind: 'physical',
    interval: 0.8,
    range: 5.0,
    projectile: 'bullet',
    speed: 26,
    pierce: 3,
    description: 'A needle fired hard enough to thread four bodies at once.',
  },
  {
    id: 'glacier-rune',
    name: 'Glacier Rune',
    glyph: 'Gr',
    classes: ['arcanist', 'frost'],
    tier: 2,
    damage: 16,
    kind: 'magic',
    interval: 0.85,
    range: 4.0,
    projectile: 'lob',
    speed: 9,
    aoe: 1.2,
    description: 'Shatters a patch of ground into rime.',
  },
  {
    id: 'static-spore',
    name: 'Static Spore',
    glyph: 'Sp',
    classes: ['venom', 'tempest'],
    tier: 2,
    damage: 15,
    kind: 'magic',
    interval: 0.7,
    range: 3.8,
    projectile: 'chain',
    speed: 22,
    description: 'Spores that discharge as they spread.',
  },
  {
    id: 'frost-mint',
    name: 'Frost Mint',
    glyph: 'Fm',
    classes: ['frost', 'alchemist'],
    tier: 2,
    damage: 1,
    kind: 'magic',
    interval: 0.1,
    range: 3.9,
    projectile: 'laser',
    speed: 0,
    rampPerTick: 0.04,
    rampMax: 2.5,
    scaling: 'scaler',
    description: 'A cutting beam that bites deeper the longer it stays put.',
  },
  {
    id: 'storm-pylon',
    name: 'Storm Pylon',
    glyph: 'Sy',
    classes: ['tempest', 'beacon'],
    tier: 2,
    damage: 17,
    kind: 'magic',
    interval: 0.8,
    range: 4.0,
    projectile: 'chain',
    speed: 24,
    description: 'Feeds its neighbours and forks its own bolts.',
  },
  {
    id: 'coin-hive',
    name: 'Coin Hive',
    glyph: 'Ch',
    classes: ['alchemist', 'swarm'],
    tier: 2,
    damage: 25,
    kind: 'physical',
    interval: 0.65,
    range: 3.4,
    projectile: 'bullet',
    speed: 12,
    salvo: 4,
    spreadDeg: 20,
    scaling: 'rush',
    description: 'Sprays four struck coins in a widening fan.',
  },
  {
    id: 'umbral-beacon',
    name: 'Umbral Beacon',
    glyph: 'Ub',
    classes: ['beacon', 'void'],
    tier: 2,
    damage: 22,
    kind: 'magic',
    interval: 1.05,
    range: 3.8,
    projectile: 'homing',
    speed: 4,
    targeting: 'weakest',
    accel: 18,
    aoe: 0.9,
    description: 'Lobs a slow shadow-missile that gathers speed and bursts on arrival.',
  },
  {
    id: 'bastion-nest',
    name: 'Bastion Nest',
    glyph: 'Bn',
    classes: ['swarm', 'warden'],
    tier: 2,
    damage: 10,
    kind: 'physical',
    interval: 1.5,
    range: 3.3,
    projectile: 'bullet',
    speed: 11,
    burst: 3,
    burstGapTicks: 3,
    scaling: 'rush',
    description: 'A three-round volley from a nest that then goes quiet.',
  },
  {
    id: 'eclipse-rifle',
    name: 'Eclipse Rifle',
    glyph: 'Er',
    classes: ['void', 'marksman'],
    tier: 2,
    damage: 24,
    kind: 'magic',
    interval: 1.3,
    range: 5.4,
    projectile: 'beam',
    speed: 0,
    targeting: 'strongest',
    scaling: 'scaler',
    description: 'Instant, silent, and partial to the biggest thing on screen.',
  },

  // ── Tier 3 — ring distance 3 ───────────────────────────────────────────────────────
  {
    id: 'plague-wall',
    name: 'Plague Wall',
    glyph: 'Pw',
    classes: ['warden', 'venom'],
    tier: 3,
    damage: 32,
    kind: 'magic',
    interval: 1.1,
    range: 4.2,
    projectile: 'lob',
    speed: 8,
    aoe: 1.5,
    scaling: 'rush',
    description: 'A rampart that weeps contagion.',
  },
  {
    id: 'frostmark',
    name: 'Frostmark',
    glyph: 'Fk',
    classes: ['marksman', 'frost'],
    tier: 3,
    damage: 34,
    kind: 'physical',
    interval: 1.2,
    range: 6.2,
    projectile: 'bullet',
    speed: 30,
    pierce: 3,
    scaling: 'scaler',
    description: 'A rail-driven spike that leaves a hole in everything on its line.',
  },
  {
    id: 'arc-sanctum',
    name: 'Arc Sanctum',
    glyph: 'As',
    classes: ['arcanist', 'tempest'],
    tier: 3,
    damage: 30,
    kind: 'magic',
    interval: 0.75,
    range: 4.4,
    projectile: 'chain',
    speed: 26,
    scaling: 'scaler',
    description: 'A cathedral of standing current.',
  },
  {
    id: 'aurum-rot',
    name: 'Aurum Rot',
    glyph: 'Ar',
    classes: ['venom', 'alchemist'],
    tier: 3,
    damage: 28,
    kind: 'magic',
    interval: 0.85,
    range: 4.0,
    projectile: 'lob',
    speed: 9,
    aoe: 1.3,
    description: 'Transmutes the dying into income.',
  },
  {
    id: 'winter-choir',
    name: 'Winter Choir',
    glyph: 'Wc',
    classes: ['frost', 'beacon'],
    tier: 3,
    damage: 2,
    kind: 'magic',
    interval: 0.1,
    range: 4.6,
    projectile: 'laser',
    speed: 0,
    rampPerTick: 0.05,
    rampMax: 3,
    scaling: 'scaler',
    description: 'A sustained choral beam that builds to something unsurvivable.',
  },
  {
    id: 'thunder-brood',
    name: 'Thunder Brood',
    glyph: 'Tb',
    classes: ['tempest', 'swarm'],
    tier: 3,
    damage: 25,
    kind: 'magic',
    interval: 0.6,
    range: 4.0,
    projectile: 'chain',
    speed: 26,
    scaling: 'scaler',
    description: 'Drones that carry the storm with them.',
  },
  {
    id: 'entropy-mint',
    name: 'Entropy Mint',
    glyph: 'Em',
    classes: ['alchemist', 'void'],
    tier: 3,
    damage: 34,
    kind: 'magic',
    interval: 1.15,
    range: 4.2,
    projectile: 'homing',
    speed: 4,
    targeting: 'weakest',
    accel: 24,
    aoe: 1.0,
    scaling: 'scaler',
    description: 'Prints a homing charge that accelerates until it detonates.',
  },
  {
    id: 'aegis-lantern',
    name: 'Aegis Lantern',
    glyph: 'Al',
    classes: ['beacon', 'warden'],
    tier: 3,
    damage: 24,
    kind: 'physical',
    interval: 2.2,
    range: 4.4,
    projectile: 'bullet',
    speed: 12,
    burst: 3,
    burstGapTicks: 4,
    description: 'Three measured pulses, then a long and deliberate recharge.',
  },
  {
    id: 'wasp-battery',
    name: 'Wasp Battery',
    glyph: 'Wb',
    classes: ['swarm', 'marksman'],
    tier: 3,
    damage: 100,
    kind: 'physical',
    interval: 0.55,
    range: 5.0,
    projectile: 'bullet',
    speed: 20,
    salvo: 5,
    spreadDeg: 24,
    scaling: 'rush',
    description: 'Flak. Five stingers at once, and range is the only thing it lacks.',
  },
  {
    id: 'oblivion-rune',
    name: 'Oblivion Rune',
    glyph: 'Or',
    classes: ['void', 'arcanist'],
    tier: 3,
    damage: 42,
    kind: 'magic',
    interval: 1.4,
    range: 4.6,
    projectile: 'homing',
    speed: 3,
    targeting: 'strongest',
    accel: 30,
    aoe: 1.4,
    scaling: 'scaler',
    description: 'A rune that leaves the barrel slowly and arrives as an ending.',
  },

  // ── The archetype expansion (backlog B-15/B-17/B-18/B-19) ──────────────────────────
  // Support towers never fire; summoners fight through their brood; zone towers strike
  // a telegraphed area. These break the original 6-per-class circulant on purpose —
  // V-2 needs ≥ 6 per class, and every class stays at or above it.

  // B-15 — pure buffers. Their cost is the board slot and the class counts they occupy.
  //
  // Each idles at HALF the aura it used to hold and can be fired once a wave to pay out
  // at DOUBLE it (playtest request) — a factor of four between the two states, which is
  // `SUPPORT_SURGE_FACTOR`. The passive alone is deliberately weaker than what it
  // replaced: a support tower that was already worth its slot while idle would make the
  // button a bonus rather than a decision, and the decision is the point.
  {
    id: 'signal-lens',
    name: 'Signal Lens',
    glyph: 'Sl',
    classes: ['beacon', 'marksman'],
    tier: 1,
    damage: 0,
    kind: 'physical',
    interval: 0,
    range: 3.0,
    projectile: 'beam',
    speed: 0,
    support: true,
    // radiusTiles is dead weight when radiusFromRange is set (the schema still wants it
    // positive) — the aura covers the tower's printed range, same as the Beacon tiers.
    innate: [
      { k: 'aura', radiusTiles: 0.5, radiusFromRange: true, stat: 'damageMultiplier', op: 'mul', value: 1.05 },
    ],
    scaling: 'scaler',
    description:
      'Fires nothing. Towers within its range hit 5% harder — 20% while the lens is focused.',
  },
  {
    id: 'tempo-shrine',
    name: 'Tempo Shrine',
    glyph: 'Ts',
    classes: ['beacon', 'tempest'],
    tier: 2,
    damage: 0,
    kind: 'physical',
    interval: 0,
    range: 3.2,
    projectile: 'beam',
    speed: 0,
    support: true,
    innate: [
      { k: 'aura', radiusTiles: 0.5, radiusFromRange: true, stat: 'attackSpeedMultiplier', op: 'mul', value: 1.075 },
    ],
    description:
      'A metronome shrine. Towers in range fire 7.5% faster — 30% while the tempo doubles.',
  },
  {
    id: 'hoarfrost-idol',
    name: 'Hoarfrost Idol',
    glyph: 'Hi',
    classes: ['frost', 'warden'],
    tier: 2,
    damage: 0,
    kind: 'magic',
    interval: 0,
    range: 2.5,
    projectile: 'beam',
    speed: 0,
    support: true,
    innate: [
      { k: 'auraStatus', radiusTiles: 2.5, status: 'chill', magnitude: 0.075, durationTicks: 30 },
    ],
    description:
      'Attacks nothing, forgives nothing: everything near it walks 7.5% slower — 30% when it bites.',
  },

  // B-17 — broodmothers. The weapon is a formality; the brood is the point.
  {
    id: 'brood-cauldron',
    name: 'Brood Cauldron',
    glyph: 'Br',
    classes: ['swarm', 'venom'],
    tier: 2,
    damage: 20,
    kind: 'magic',
    interval: 1.1,
    range: 3.4,
    projectile: 'lob',
    speed: 8,
    aoe: 0.7,
    innate: [
      { k: 'summon', everyTicks: 120, flatDamage: 4, damageFromOwnerPercent: 0.5, lifetimeTicks: 480 },
    ],
    description: 'Simmers over. Every 2s a mite crawls out and goes looking for someone.',
  },
  {
    id: 'star-hatchery',
    name: 'Star Hatchery',
    glyph: 'Sh',
    classes: ['swarm', 'arcanist'],
    tier: 3,
    damage: 18,
    kind: 'magic',
    interval: 1.2,
    range: 3.8,
    projectile: 'bullet',
    speed: 11,
    innate: [
      { k: 'summon', everyTicks: 75, flatDamage: 6, damageFromOwnerPercent: 0.6, lifetimeTicks: 480 },
    ],
    scaling: 'scaler',
    description: 'Incubates small stars. They hatch hungry, and they hatch often.',
  },

  // B-18/B-19 — zone artillery. The cast telegraphs, then the ground itself attacks.
  {
    id: 'kiln-sigil',
    name: 'Kiln Sigil',
    glyph: 'Ks',
    classes: ['alchemist', 'warden'],
    tier: 2,
    // Was 20 damage over a 1.3 radius, and it took 33% of a board's damage on average
    // WHENEVER it was placed — measured across 24 dailies, against a 10-18% pack. A zone
    // is the one weapon whose output is multiplicative in enemy count rather than linear,
    // so on a packed wave it compounds while everything else stays flat; that identity is
    // fine, but it was arriving from tier 2 with better numbers than the tier-3 zone
    // tower (hunting-hex: 9 damage, 1.0 radius, 8 gold) on every axis at 5 gold.
    // Now ~28 DPS per target in the same 1.0 radius as hunting-hex's ~33, and without its
    // tracking — cheaper, weaker, static. See docs/BACKLOG.md.
    damage: 12,
    kind: 'magic',
    interval: 2.6,
    range: 4.0,
    projectile: 'zone',
    speed: 0,
    aoe: 1.0,
    zone: { windup: 36, active: 150, strikeEvery: 25 },
    scaling: 'rush',
    description: 'Brands a kiln-mark on the ground; everything standing in it is stock for the fire.',
  },
  {
    id: 'hunting-hex',
    name: 'Hunting Hex',
    glyph: 'Hx',
    classes: ['void', 'frost'],
    tier: 3,
    damage: 9,
    kind: 'magic',
    interval: 3.0,
    range: 4.6,
    projectile: 'zone',
    speed: 0,
    aoe: 1.0,
    zone: { windup: 24, active: 210, strikeEvery: 18, trackSpeed: 1.6 },
    targeting: 'strongest',
    description: 'A hex that follows. Slower than its prey, but it does not stop and it does not miss.',
  },
];

export const TOWERS: TowerDef[] = SPECS.map(build);
